/**
 * QR-Code Generierung für Überweisungen
 * Dokument hochladen → OCR extrahiert Zahlungsdaten → QR-Code generieren
 * Oder: Daten aus Beleg-Tab übernehmen (via URL-Parameter)
 * Oder: Direkte Überweisung via finAPI
 */

import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  QrCode, Upload, FileText, Loader2, CheckCircle2,
  AlertCircle, Euro, Building2, Hash, Edit3, RefreshCw,
  History, Calendar, ChevronDown, ChevronUp, RotateCcw,
  Send, Banknote, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function QRCodeGeneratorPage() {
  const [searchParams] = useSearchParams();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // FinAPI State
  const [finAPIStatus, setFinAPIStatus] = useState(null);
  const [konten, setKonten] = useState([]);
  const [selectedKonto, setSelectedKonto] = useState('');
  const [sendingPayment, setSendingPayment] = useState(false);
  
  // Editierbare Felder
  const [formData, setFormData] = useState({
    empfaenger_name: '',
    empfaenger_iban: '',
    empfaenger_bic: '',
    betrag: '',
    verwendungszweck: '',
  });

  // Verlauf laden
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API_URL}/api/qr-ueberweisung/history?limit=10`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error('Verlauf laden Fehler:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // Verlauf laden beim Mount
  useEffect(() => {
    loadHistory();
    loadFinAPIStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadHistory]);

  // FinAPI Status & Konten laden
  const loadFinAPIStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/finapi/status`);
      if (res.ok) {
        const data = await res.json();
        setFinAPIStatus(data);
        
        // Wenn finAPI verfügbar, lade Konten
        if (data.is_configured || data.mode === 'mock') {
          loadKonten();
        }
      }
    } catch (error) {
      console.error('FinAPI Status Fehler:', error);
    }
  };

  const loadKonten = async () => {
    try {
      // Lade Konten aus Buchhaltung
      const res = await fetch(`${API_URL}/api/buchhaltung/konten`);
      if (res.ok) {
        const data = await res.json();
        // Nur Bankkonten mit IBAN anzeigen
        const bankkonten = (data || []).filter(k => k.iban || k.typ === 'girokonto');
        setKonten(bankkonten);
      }
    } catch (error) {
      console.error('Konten laden Fehler:', error);
    }
  };

  // Direkte Überweisung via FinAPI
  const handleFinAPIPayment = async () => {
    if (!formData.empfaenger_iban || !formData.betrag || !formData.empfaenger_name) {
      toast.error('IBAN, Empfänger und Betrag sind Pflichtfelder');
      return;
    }
    
    if (!selectedKonto) {
      toast.error('Bitte wählen Sie ein Quellkonto aus');
      return;
    }
    
    setSendingPayment(true);
    
    try {
      const res = await fetch(`${API_URL}/api/finapi/payment/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          klient_id: 'default',
          source_account_id: selectedKonto,
          recipient_name: formData.empfaenger_name,
          recipient_iban: formData.empfaenger_iban,
          amount: parseFloat(formData.betrag),
          purpose: formData.verwendungszweck || '',
          recipient_bic: formData.empfaenger_bic || '',
        }),
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Überweisung fehlgeschlagen');
      }
      
      if (data.is_mock) {
        toast.success('Mock-Überweisung erfolgreich! (Test-Modus)');
      } else {
        toast.success(data.message || 'Überweisung initiiert!');
      }
      
      // Verlauf neu laden
      loadHistory();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingPayment(false);
    }
  };

  // URL-Parameter verarbeiten (von Beleg-Tab)
  useEffect(() => {
    const betrag = searchParams.get('betrag');
    const empfaenger = searchParams.get('empfaenger');
    const verwendungszweck = searchParams.get('verwendungszweck');
    const iban = searchParams.get('iban');
    
    if (betrag || empfaenger || verwendungszweck || iban) {
      setFormData(prev => ({
        ...prev,
        betrag: betrag || prev.betrag,
        empfaenger_name: empfaenger || prev.empfaenger_name,
        verwendungszweck: verwendungszweck || prev.verwendungszweck,
        empfaenger_iban: iban || prev.empfaenger_iban,
      }));
      setEditMode(true);
      toast.info('Daten aus Beleg übernommen. Bitte IBAN ergänzen.');
    }
  }, [searchParams]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error('Nur PDF, PNG oder JPG Dateien erlaubt');
        return;
      }
      setFile(selectedFile);
      setResult(null);
      setEditMode(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    
    setUploading(true);
    setResult(null);
    
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      
      const res = await fetch(`${API_URL}/api/qr-ueberweisung/extract-from-document`, {
        method: 'POST',
        body: formDataObj,
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Fehler bei der Verarbeitung');
      }
      
      setResult(data);
      
      // Formular mit extrahierten Daten füllen
      if (data.zahlungsdaten) {
        setFormData({
          empfaenger_name: data.zahlungsdaten.empfaenger_name || '',
          empfaenger_iban: data.zahlungsdaten.empfaenger_iban || '',
          empfaenger_bic: data.zahlungsdaten.empfaenger_bic || '',
          betrag: data.zahlungsdaten.betrag?.toString() || '',
          verwendungszweck: data.zahlungsdaten.verwendungszweck || data.zahlungsdaten.rechnungsnummer || '',
        });
      }
      
      if (data.qr_ready) {
        toast.success('QR-Code erfolgreich generiert!');
      } else {
        toast.warning('Einige Daten konnten nicht extrahiert werden. Bitte ergänzen.');
        setEditMode(true);
      }
    } catch (error) {
      console.error('Upload Fehler:', error);
      toast.error(error.message || 'Verarbeitung fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateWithEdits = async () => {
    if (!formData.empfaenger_iban || !formData.betrag || !formData.empfaenger_name) {
      toast.error('IBAN, Empfänger und Betrag sind Pflichtfelder');
      return;
    }
    
    setGenerating(true);
    
    try {
      const res = await fetch(`${API_URL}/api/qr-ueberweisung/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          empfaenger_name: formData.empfaenger_name,
          empfaenger_iban: formData.empfaenger_iban,
          empfaenger_bic: formData.empfaenger_bic || null,
          betrag: parseFloat(formData.betrag),
          verwendungszweck: formData.verwendungszweck || null,
        }),
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'QR-Code Generierung fehlgeschlagen');
      }
      
      setResult(prev => ({
        ...prev,
        qr_code_base64: data.qr_code_base64,
        qr_ready: true,
        zahlungsdaten: data.zahlungsdaten,
      }));
      setEditMode(false);
      toast.success('QR-Code generiert!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setEditMode(false);
    setFormData({
      empfaenger_name: '',
      empfaenger_iban: '',
      empfaenger_bic: '',
      betrag: '',
      verwendungszweck: '',
    });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="qr-generator-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
          <QrCode className="w-6 h-6 text-violet-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">QR-Code Generierung</h1>
          <p className="text-sm text-slate-500">
            Dokument hochladen → Zahlungsdaten automatisch extrahieren → QR-Code scannen
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Upload & Extraktion */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-500" />
              Dokument hochladen
            </CardTitle>
            <CardDescription>
              Rechnung, Beleg oder Email als PDF oder Bild
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                file ? 'border-green-300 bg-green-50' : 'border-slate-200 hover:border-blue-300'
              }`}
            >
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                data-testid="file-upload-input"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    <FileText className="w-10 h-10 text-green-500" />
                    <p className="font-medium text-green-700">{file.name}</p>
                    <p className="text-xs text-slate-400">Klicken um zu ändern</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-10 h-10 text-slate-400" />
                    <p className="font-medium text-slate-600">Datei auswählen</p>
                    <p className="text-xs text-slate-400">PDF, PNG, JPG</p>
                  </div>
                )}
              </label>
            </div>
            
            <Button 
              onClick={handleUpload} 
              disabled={!file || uploading}
              className="w-full"
              data-testid="upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verarbeite...
                </>
              ) : (
                <>
                  <QrCode className="w-4 h-4 mr-2" />
                  Zahlungsdaten extrahieren & QR generieren
                </>
              )}
            </Button>

            {result && !result.qr_ready && result.fehlende_felder?.length > 0 && (
              <Alert variant="warning" className="bg-amber-50 border-amber-200">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  Fehlende Daten: {result.fehlende_felder.join(', ')}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* QR-Code Anzeige */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <QrCode className="w-5 h-5 text-violet-500" />
                QR-Code
              </CardTitle>
              {result && (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setEditMode(!editMode)}
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    {editMode ? 'Abbrechen' : 'Bearbeiten'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleReset}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!result && !editMode ? (
              <div className="h-64 flex items-center justify-center text-slate-400 text-center">
                <div>
                  <QrCode className="w-16 h-16 mx-auto mb-3 opacity-30" />
                  <p>Laden Sie ein Dokument hoch,<br />um den QR-Code zu generieren</p>
                </div>
              </div>
            ) : result?.qr_ready && result?.qr_code_base64 && !editMode ? (
              <div className="text-center space-y-4">
                <div className="bg-white p-4 rounded-lg inline-block border shadow-sm">
                  <img 
                    src={`data:image/png;base64,${result.qr_code_base64}`}
                    alt="QR-Code für Überweisung"
                    className="w-48 h-48 mx-auto"
                    data-testid="qr-code-image"
                  />
                </div>
                <p className="text-sm text-green-600 flex items-center justify-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  Scannen Sie den Code mit Ihrer Banking-App
                </p>
                
                {/* Zahlungsdaten Übersicht */}
                <div className="text-left bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Empfänger:</span>
                    <span className="font-medium">{result.zahlungsdaten?.empfaenger_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">IBAN:</span>
                    <span className="font-mono text-xs">{result.zahlungsdaten?.empfaenger_iban}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Betrag:</span>
                    <span className="font-bold text-lg text-green-700">
                      {result.zahlungsdaten?.betrag?.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                    </span>
                  </div>
                  {result.zahlungsdaten?.verwendungszweck && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Verwendungszweck:</span>
                      <span>{result.zahlungsdaten.verwendungszweck}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Edit Mode */
              <div className="space-y-4">
                <div>
                  <Label htmlFor="empfaenger_name">Empfänger *</Label>
                  <Input
                    id="empfaenger_name"
                    value={formData.empfaenger_name}
                    onChange={(e) => setFormData({ ...formData, empfaenger_name: e.target.value })}
                    placeholder="Name des Empfängers"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="empfaenger_iban">IBAN *</Label>
                  <div className="relative mt-1">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="empfaenger_iban"
                      value={formData.empfaenger_iban}
                      onChange={(e) => setFormData({ ...formData, empfaenger_iban: e.target.value.toUpperCase() })}
                      placeholder="DE89 3704 0044 0532 0130 00"
                      className="pl-10 font-mono"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="betrag">Betrag (EUR) *</Label>
                  <div className="relative mt-1">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="betrag"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={formData.betrag}
                      onChange={(e) => setFormData({ ...formData, betrag: e.target.value })}
                      placeholder="123.45"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="verwendungszweck">Verwendungszweck</Label>
                  <div className="relative mt-1">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="verwendungszweck"
                      value={formData.verwendungszweck}
                      onChange={(e) => setFormData({ ...formData, verwendungszweck: e.target.value })}
                      placeholder="Rechnungsnummer / Referenz"
                      className="pl-10"
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleGenerateWithEdits} 
                  disabled={generating}
                  className="w-full"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generiere...
                    </>
                  ) : (
                    <>
                      <QrCode className="w-4 h-4 mr-2" />
                      QR-Code generieren
                    </>
                  )}
                </Button>

                {/* FinAPI Direkt-Überweisung */}
                {finAPIStatus && (
                  <div className="border-t pt-4 mt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Banknote className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm font-medium text-slate-700">Direkte Überweisung</span>
                      {finAPIStatus.mode === 'mock' && (
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Test-Modus</Badge>
                      )}
                    </div>
                    
                    {konten.length > 0 ? (
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs">Quellkonto</Label>
                          <Select value={selectedKonto} onValueChange={setSelectedKonto}>
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Konto auswählen..." />
                            </SelectTrigger>
                            <SelectContent>
                              {konten.map(k => (
                                <SelectItem key={k.id} value={k.id}>
                                  {k.name || k.bezeichnung} {k.iban ? `(${k.iban.substring(0, 8)}...)` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          variant="outline"
                          className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={handleFinAPIPayment}
                          disabled={sendingPayment || !selectedKonto}
                          data-testid="finapi-payment-btn"
                        >
                          {sendingPayment ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Überweise...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Direkt überweisen via finAPI
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <Alert className="bg-slate-50 border-slate-200">
                        <Info className="w-4 h-4 text-slate-500" />
                        <AlertDescription className="text-slate-600 text-sm">
                          Verbinden Sie ein Bankkonto in der Buchhaltung, um direkte Überweisungen zu nutzen.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* OCR-Text Vorschau (Debug) */}
      {result?.ocr_text_preview && (
        <Card className="bg-slate-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Extrahierter Text (Vorschau)</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs text-slate-500 whitespace-pre-wrap font-mono bg-white p-3 rounded border max-h-32 overflow-auto">
              {result.ocr_text_preview}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Überweisungs-Verlauf */}
      <Card data-testid="ueberweisung-history">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-5 h-5 text-slate-500" />
              Überweisungs-Verlauf
              {history.length > 0 && (
                <Badge variant="secondary" className="ml-2">{history.length}</Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={loadHistory}
                disabled={loadingHistory}
              >
                <RefreshCw className={`w-4 h-4 ${loadingHistory ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setHistoryExpanded(!historyExpanded)}
              >
                {historyExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
          <CardDescription>
            Ihre letzten QR-Code Generierungen
          </CardDescription>
        </CardHeader>
        {(historyExpanded || history.length <= 3) && (
          <CardContent>
            {history.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">
                Noch keine QR-Codes generiert
              </p>
            ) : (
              <div className="space-y-2">
                {history.slice(0, historyExpanded ? 10 : 3).map((item) => {
                  const zd = item.zahlungsdaten || {};
                  const date = new Date(item.created_date);
                  const isReady = item.qr_ready;
                  
                  return (
                    <div
                      key={item.id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        isReady ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isReady ? 'bg-green-100' : 'bg-slate-100'
                        }`}>
                          <QrCode className={`w-5 h-5 ${isReady ? 'text-green-600' : 'text-slate-400'}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-slate-800 truncate">
                            {zd.empfaenger_name || item.dateiname || 'Unbekannt'}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                            </span>
                            {zd.empfaenger_iban && (
                              <span className="font-mono truncate max-w-[120px]">
                                {zd.empfaenger_iban.substring(0, 8)}...
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {zd.betrag && (
                          <span className="font-bold text-green-700">
                            {parseFloat(zd.betrag).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                          </span>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setFormData({
                              empfaenger_name: zd.empfaenger_name || '',
                              empfaenger_iban: zd.empfaenger_iban || '',
                              empfaenger_bic: zd.empfaenger_bic || '',
                              betrag: zd.betrag?.toString() || '',
                              verwendungszweck: zd.verwendungszweck || '',
                            });
                            setEditMode(true);
                            setResult(null);
                            toast.info('Daten übernommen');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          title="Daten übernehmen"
                        >
                          <RotateCcw className="w-4 h-4 text-violet-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {!historyExpanded && history.length > 3 && (
                  <Button
                    variant="ghost"
                    className="w-full text-sm text-violet-600"
                    onClick={() => setHistoryExpanded(true)}
                  >
                    {history.length - 3} weitere anzeigen
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
